import { useState } from 'react'
import { Link } from 'react-router-dom'
import { notifications } from '@mantine/notifications'
import { useOsAprovacoesPendentes, useOsTransicao } from '../../hooks/useOs'
import { useAuthStore } from '../../stores/authStore'
import styles from '../_shared/CrudPage.module.css'

const TIPO_LABEL: Record<string, string> = {
  preventivo: 'Preventivo',
  corretivo: 'Corretivo',
  higienizacao: 'Higienização',
  qai: 'Análise QAI',
  desinfeccao_legionella: 'Desinfecção Legionella',
  emergencial: 'Emergencial',
}

const PRIORIDADE_COR: Record<string, string> = {
  normal: '#757575',
  alta: '#F57C00',
  urgente: '#D32F2F',
  emergencial: '#B71C1C',
}

const TIPOS_MEC = 'preventivo,corretivo,higienizacao,emergencial'
const TIPOS_QAI = 'qai,desinfeccao_legionella'

export default function AprovacoesPage() {
  const perfil = useAuthStore((s) => s.user?.perfil)
  // Default: RT mecânico vê só mecânicas; RT QAI vê só QAI; admin vê tudo
  const defaultFiltro =
    perfil === 'rt_mecanico' ? 'mec' : perfil === 'rt_qai' ? 'qai' : 'todas'
  const [filtro, setFiltro] = useState<'mec' | 'qai' | 'todas'>(
    defaultFiltro as 'mec' | 'qai' | 'todas',
  )

  const tipoParam =
    filtro === 'mec' ? TIPOS_MEC : filtro === 'qai' ? TIPOS_QAI : undefined
  const { data, isLoading, refetch } = useOsAprovacoesPendentes(
    tipoParam ? { tipo_os: tipoParam } : undefined,
  )

  function AprovacaoCard({
    os,
    onAprovar,
  }: {
    os: { id: string; numero?: string | number; tipo: string; titulo?: string; data_prevista?: string; prioridade?: string }
    onAprovar: () => void
  }) {
    const transicao = useOsTransicao(os.id)

    async function handleAprovar() {
      if (!confirm(`Aprovar OS #${os.numero}? Após aprovada, fica imutável.`)) return
      try {
        await transicao.aprovar.mutateAsync()
        notifications.show({ color: 'green', message: `OS #${os.numero} aprovada.` })
        onAprovar()
      } catch (err) {
        const msg =
          (err as { response?: { data?: { status?: string } } })?.response?.data?.status ??
          'Falha ao aprovar.'
        notifications.show({ color: 'red', title: 'Falha', message: msg })
      }
    }

    return (
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <span style={{ fontSize: 11, fontWeight: 600, color: '#1565C0' }}>
            OS #{os.numero}
          </span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: PRIORIDADE_COR[os.prioridade ?? 'normal'],
            }}
          >
            {os.prioridade}
          </span>
        </div>
        <h3 className={styles.cardTitle}>{os.titulo ?? '(sem título)'}</h3>
        <p className={styles.cardSub}>
          {TIPO_LABEL[os.tipo] ?? os.tipo}
          {os.data_prevista && ` • Prev. ${os.data_prevista}`}
        </p>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
          <Link
            to={`/ordens-servico/${os.id}`}
            style={{
              flex: 1,
              padding: '0.4rem',
              background: 'white',
              color: '#1565C0',
              border: '1px solid #1565C0',
              borderRadius: 4,
              textDecoration: 'none',
              textAlign: 'center',
              fontSize: '0.85rem',
            }}
          >
            Ver detalhes
          </Link>
          <button
            type="button"
            onClick={handleAprovar}
            disabled={transicao.aprovar.isPending}
            data-testid={`btn-aprovar-${os.id}`}
            style={{
              flex: 1,
              padding: '0.4rem',
              background: '#2E7D32',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: 600,
            }}
          >
            {transicao.aprovar.isPending ? '...' : '✓ Aprovar'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Aprovações pendentes</h1>
          <p className={styles.subtitle}>
            OS concluídas aguardando assinatura do RT supervisor (R7b.2).
          </p>
        </div>
      </header>

      <div className={styles.toolbar}>
        <div role="tablist" style={{ display: 'flex', gap: '0.5rem' }}>
          {(['mec', 'qai', 'todas'] as const).map((v) => (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={filtro === v}
              onClick={() => setFiltro(v)}
              data-testid={`tab-aprovacoes-${v}`}
              style={{
                padding: '0.4rem 1rem',
                background: filtro === v ? '#1565C0' : 'white',
                color: filtro === v ? 'white' : '#1565C0',
                border: '1px solid #1565C0',
                borderRadius: 4,
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.85rem',
              }}
            >
              {v === 'mec' ? 'Mecânicas' : v === 'qai' ? 'QAI' : 'Todas'}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className={styles.loading}>Carregando aprovações pendentes...</div>
      ) : (data?.results.length ?? 0) === 0 ? (
        <p className={styles.empty}>
          🎉 Nenhuma OS aguardando sua aprovação no momento.
        </p>
      ) : (
        <>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
            {data?.count ?? 0} OS{(data?.count ?? 0) === 1 ? '' : 's'} aguardando
          </p>
          <div className={styles.grid}>
            {data?.results.map((os) => (
              <AprovacaoCard key={os.id} os={os} onAprovar={() => refetch()} />
            ))}
          </div>
        </>
      )}
    </div>
  )
}
